import React, { useState, useRef, useEffect, ReactNode } from "react";
import { AvailabilityCalendar, AvailabilityCalendarDatePicker, CalendarTimeType } from "mueller-controls";
import MCard from "components/Shared/MCard.tsx";
import moment, { Moment } from "moment";
import PropTypes from "prop-types";
import { getInspectorsForCase } from "requests/homeowner/fieldReps";
import { getCalendarEventsByEmployeeId } from "requests/employeeCalendarEvent";
import { getCaseDistances } from "requests/homeowner/ho";
import { Button, MSpinner } from "components/Shared";
import { calculateLetterGrade } from "components/Shared/TimeLogs/Definitions";
import { SecondaryStatus } from "typeConstants/caseStatusCodes";
import { getHolidaysBetweenDates } from "requests/holiday/index.ts";
import { getHolidayMouseoverText } from "components/Employee/EmployeeCalendar.tsx";
import {
  HolidayDto,
  InspectorForCase,
  InspectorForCaseAvailableDateReason,
  InspectorsForCaseResponse,
  ControlNumberDistancePair,
} from "interfaces/Mueller.Business.Models";
import { Employee, CalendarEntry } from "mueller-controls/dist/src/shared/classes";
import { createRequestCoalescer } from "tools/requestCoalescer";

interface InspectorEmployee {
  employeeId: number;
  className?: string;
  isTrainee: boolean;
  daysAvailability: InspectorForCaseAvailableDateReason[];
  getDisplayName: (distanceToCase?: number) => string;
}

interface AvailabilityByDay {
  inspector: InspectorEmployee;
  availability: InspectorForCaseAvailableDateReason;
  nextAvailableDay?: InspectorForCaseAvailableDateReason;
  missingDesignation: boolean;
}

// TODO: export OverlayEventIdLink from mueller-controls
interface OverlayEventIdLink {
  eventId: number;
  overlayText: string;
}

const holidaysCoalescer = createRequestCoalescer<HolidayDto[], string>();
const inspectorsForCaseCoalescer = createRequestCoalescer<InspectorsForCaseResponse, string>();
const distancesCoalescer = createRequestCoalescer<OverlayEventIdLink[], string>();
const employeeCalendarEventsCoalescer = createRequestCoalescer<CalendarEntry[], string>();
const activeRequest = { current: true };

const FieldRepAvailabilityView = ({ controlIdNumber, webStatus }: { controlIdNumber: number; webStatus: string }) => {
  const shouldLoadAvailabilityOnMount = webStatus !== SecondaryStatus.RECEIVED;
  const [serverInspectorList, setServerInspectorList] = useState<InspectorsForCaseResponse>();
  const [eligibleFieldReps, setEligibleFieldReps] = useState<InspectorEmployee[]>([]); // List of FRs referenced to load calendar events
  const [eligibleAvailabilityByDay, setEligibleAvailabilityByDay] = useState<AvailabilityByDay[]>(); // List of eligible frs for the curDate
  const [missingDesignationAvailabilityByDay, setMissingDesignationAvailabilityByDay] = useState<AvailabilityByDay[]>(); // List of missing designation frs for the curDate
  const [missingDesignationFRs, setMissingDesignationFRs] = useState<InspectorEmployee[]>([]);
  const [excludedFRs, setExcludedFRs] = useState<InspectorEmployee[]>([]);
  const [calendarEntries, setCalendarEntries] = useState<CalendarEntry[]>([]);
  const [monthsLoaded, setMonthsLoaded] = useState<number[]>([]);
  const [curDate, setCurDate] = useState(moment());
  const [distances, setDistances] = useState<OverlayEventIdLink[]>([]);
  const [requestingFRs, setRequestingFRs] = useState<boolean>(false);
  const [holidays, setHolidays] = useState<HolidayDto[]>();
  const [shouldLoadAvailability, setShouldLoadAvailability] = useState<boolean>(shouldLoadAvailabilityOnMount);

  const instance = useRef({ loadingCalendarCount: 0 });
  const mounted = useRef(true);
  const holidayRequestCleanup = useRef<() => void>(() => {});
  const inspectorsRequestCleanup = useRef<() => void>(() => {});
  const distancesRequestCleanup = useRef<() => void>(() => {});
  const calendarRequestCleanups = useRef<Array<() => void>>([]);

  useEffect(() => {
    setShouldLoadAvailability(shouldLoadAvailabilityOnMount);
  }, [controlIdNumber, shouldLoadAvailabilityOnMount]);

  useEffect(() => {
    if (!shouldLoadAvailability) {
      return;
    }

    const holidayLoadRangeYears = 2;
    const holidayStart = moment().subtract(holidayLoadRangeYears, "year").startOf("day").toDate();
    const holidayEnd = moment().add(holidayLoadRangeYears, "year").endOf("day").toDate();
    const holidayKey = `${holidayStart.toISOString()}|${holidayEnd.toISOString()}`;

    holidayRequestCleanup.current = holidaysCoalescer.run(
      holidayKey,
      (resolve, reject) => getHolidaysBetweenDates(holidayStart, holidayEnd, resolve, activeRequest, reject),
      (data) => {
        if (mounted.current) {
          setHolidays(data);
        }
      },
    );
  }, [shouldLoadAvailability]);

  useEffect(() => () => {
    mounted.current = false;
    holidayRequestCleanup.current();
    inspectorsRequestCleanup.current();
    distancesRequestCleanup.current();
    calendarRequestCleanups.current.forEach((cleanup) => cleanup());
    calendarRequestCleanups.current = [];
  }, []);

  useEffect(() => {
    if (!shouldLoadAvailability) {
      return;
    }

    if (instance.current.loadingCalendarCount === 0) {
      let controlIdNumbers = calendarEntries
        .filter(
          (entry) =>
            entry.timeType === CalendarTimeType.FieldInspectionAppointment ||
            entry.timeType === CalendarTimeType.AppraisalAppointment
        )
        .map((entry) => entry.id);

      if (controlIdNumbers.length === 0) {
        distancesRequestCleanup.current();
        setDistances([]);
        return;
      }

      const distanceKey = `${controlIdNumber}|${[...controlIdNumbers].sort((left, right) => left - right).join(",")}`;
      distancesRequestCleanup.current();
      distancesRequestCleanup.current = distancesCoalescer.run(
        distanceKey,
        (resolve, reject) =>
          getCaseDistances(
            controlIdNumber,
            controlIdNumbers,
            (distancePairs: ControlNumberDistancePair[]) => {
              resolve(
                distancePairs.map((d) => ({
                  eventId: d.destinationControlIdNumber,
                  overlayText: `${d.distance}`,
                }))
              );
            },
            activeRequest,
            reject,
          ),
        (data) => {
          if (mounted.current) {
            setDistances(data);
          }
        },
      );
    }
  }, [calendarEntries, controlIdNumber, shouldLoadAvailability]);

  useEffect(() => {
    if (!shouldLoadAvailability || !controlIdNumber) return;
    setRequestingFRs(true);
    let startDate = moment().subtract(2, "months").startOf("day").toDate();
    let endDate = moment().add(4, "months").endOf("day").toDate();
    const requestKey = `${controlIdNumber}|${startDate.toISOString()}|${endDate.toISOString()}`;
    inspectorsRequestCleanup.current();
    inspectorsRequestCleanup.current = inspectorsForCaseCoalescer.run(
      requestKey,
      (resolve, reject) => getInspectorsForCase({ controlIdNumber, startDate, endDate }, resolve, activeRequest, reject),
      (data) => {
        if (mounted.current) {
          setServerInspectorList(data);
        }
      },
      () => {
        if (mounted.current) {
          setRequestingFRs(false);
        }
      },
    );
  }, [controlIdNumber, shouldLoadAvailability]);

  useEffect(() => {
    if (!serverInspectorList) return;

    // Trainees should be hidden from missing designation FRs as they won't be able to get the designation quick enough
    let missingDesignation = serverInspectorList.missingDesignationInspectors?.filter(fr => !fr.isTrainee).map(asEmployee) ?? [];
    setMissingDesignationFRs(missingDesignation);
    setExcludedFRs(serverInspectorList?.excludedInspectors?.map(asEmployee));
    setEligibleFieldReps(serverInspectorList.eligibleInspectors.map(asEmployee));
    setServerInspectorList(serverInspectorList);
  }, [serverInspectorList]);

  useEffect(() => {
    if (!shouldLoadAvailability) {
      return;
    }

    loadAllEntriesForFieldReps(
      curDate.clone().startOf("month"),
      curDate.clone().add(30, "days").endOf("day"),
    );
  }, [curDate, eligibleFieldReps, missingDesignationFRs, shouldLoadAvailability]);

  // set the new entries in state, combine with any old entries that we didnt get fresh data for
  const combineEntries = (serverEntries: CalendarEntry[]) => {
    setCalendarEntries((previousEntries) => [
      ...serverEntries,
      ...previousEntries.filter((e) => !serverEntries.some((se) => se.timeType === e.timeType && se.id === e.id)),
    ]);
  };

  const getMonthNumber = (momentDate) => parseInt(momentDate.format("YYYYMM"));

  const getInspectorCoverageForDay = (daysAvailability: InspectorForCaseAvailableDateReason[], day: Moment) => {
    // no matter how hard i tried, i couldnt get this comparison to work with moment.isSame because of timezone issues, but this format works great
    let date = daysAvailability?.find((d) => moment.utc(d.date).format("YYYY-MM-DD") === day.format('YYYY-MM-DD'));
    if (date) return date;
    else return null;
  };

  const asEmployee = (fr: InspectorForCase): InspectorEmployee => {
    let letterGrade = calculateLetterGrade(
      fr.costEfficiencyScore * 100,
      serverInspectorList.inspectorTimeEfficiencyFailingScore
    );

    const getDisplayName = (dist = null) => {
      let name = `${fr.fullName} (${fr.number}) (${letterGrade}`;

      if (dist || dist === 0) {
        name += `; ${dist?.toFixed(1)}mi)`;
      } else {
        name += ")";
      }

      return name;
    };

    return {
      getDisplayName: getDisplayName,
      employeeId: fr.employeeId,
      className: fr.canBeAssignedCases ? null : "ineligible",
      daysAvailability: fr.daysAvailability,
      isTrainee: fr.isTrainee,
    };
  };

  useEffect(() => {
    // Back end returns null if there's no case geocode
    if (serverInspectorList) {
      const getAvailabilityByDay = (inspectors: InspectorForCase[]) => {
        let availByDay: AvailabilityByDay[] =
          inspectors?.map((fr) => ({
            inspector: asEmployee(fr),
            availability: getInspectorCoverageForDay(fr.daysAvailability, curDate),
            missingDesignation: false,
          })) ?? [];

        availByDay.forEach(fr => {
          if (!fr.availability?.isAvailable) {
            fr.nextAvailableDay = fr.inspector.daysAvailability.find(
              d => moment(d.date) > curDate && d.isAvailable
            );
          }
        });

        return availByDay;
      };

      setEligibleAvailabilityByDay(getAvailabilityByDay(serverInspectorList.eligibleInspectors));
      setMissingDesignationAvailabilityByDay(getAvailabilityByDay(serverInspectorList.missingDesignationInspectors));
    }
    setRequestingFRs(false);
  }, [serverInspectorList, curDate]);

  const onDateChanged = (date) => {
    let startOfMonth = date.clone().startOf("month");
    let endOfMonth = date.clone().endOf("month");
    let monthNum = getMonthNumber(startOfMonth);
    setCurDate(date);

    if (!monthsLoaded.some((m) => m === monthNum)) {
      setMonthsLoaded([...monthsLoaded, monthNum]);
      loadAllEntriesForFieldReps(startOfMonth, endOfMonth);
    }
  };

  const loadAllEntriesForFieldReps = (startDate, endDate) => {
    calendarRequestCleanups.current.forEach((cleanup) => cleanup());
    calendarRequestCleanups.current = [];

    [...eligibleFieldReps, ...missingDesignationFRs].forEach((fr) => {
      instance.current.loadingCalendarCount++;
      const requestKey = `${fr.employeeId}|${startDate.toISOString()}|${endDate.toISOString()}`;

      const unsubscribe = employeeCalendarEventsCoalescer.run(
        requestKey,
        (resolve, reject) => getCalendarEventsByEmployeeId(
          fr.employeeId,
          startDate,
          endDate,
          resolve,
          reject,
          activeRequest,
        ),
        (entries) => {
          if (mounted.current) {
            combineEntries(entries);
          }
          instance.current.loadingCalendarCount--;
        },
        () => {
          instance.current.loadingCalendarCount--;
        },
      );

      calendarRequestCleanups.current.push(unsubscribe);
    });
  };

  const getCardContent = () => {
    if (!shouldLoadAvailability) {
      return (
        <div className="fr-availability-request-container">
          <Button onClick={() => setShouldLoadAvailability(true)}>Get FR Availability</Button>
        </div>
      );
    }

    if (requestingFRs || !holidays || !eligibleAvailabilityByDay) {
      return <MSpinner />;
    }

    const travelingFRs = eligibleAvailabilityByDay.filter((fr) => !fr.availability?.isAvailable);
    let hasIneligibleFRs = missingDesignationFRs.length > 0 || excludedFRs.length > 0 || travelingFRs.length > 0;

    const calendarFRsEligible = eligibleAvailabilityByDay.filter((fr) => fr.availability?.isAvailable);

    const getCalendar = (frs: AvailabilityByDay[]) => (
      <AvailabilityCalendar
        // @ts-ignore
        date={curDate}
        employees={frs.map(fr => {
          let isTraveling = fr?.availability?.secondaryStartingLocation === "Travel";
          let distanceToCase = fr?.availability?.distanceToCase;

          return {
            employeeId: fr.inspector.employeeId,
            name: (isTraveling ? "TRAVELER: " : "") + fr.inspector.getDisplayName(distanceToCase),
            distanceToCase,
          };
        }).sort((fr1, fr2) => fr1.distanceToCase - fr2.distanceToCase)}
        calendarEntries={calendarEntries}
        eventOverlays={distances}
        holidays={holidays}
        getHolidayMouseoverText={getHolidayMouseoverText}
      />
    );

    let calToShow = null;
    let isShowingMissingDesignationCal = false;
    
    let missingDesignationAvailableThisDay = missingDesignationAvailabilityByDay.filter((fr) => fr.availability?.isAvailable);
    let missingDesignationTraveling = missingDesignationAvailabilityByDay.filter((fr) => !fr.availability?.isAvailable);

    let frListToUseForTooltips = travelingFRs.map(fr => ({ ...fr, missingDesignation: false })).concat(missingDesignationTraveling.map(fr => ({ ...fr, missingDesignation: true })));

    if (calendarFRsEligible.length > 0) {
      calToShow = getCalendar(calendarFRsEligible);
    } else if (missingDesignationAvailableThisDay.length > 0) {
      const missingDesignationCal = getCalendar(missingDesignationAvailableThisDay);
      isShowingMissingDesignationCal = true;

      calToShow = (
        <>
          <div className="no-eligible-field-reps">
            Attention! FRs below are missing the proper designation. Schedule appointment at least 2 weeks out.
          </div>
          {missingDesignationCal}
        </>
      );
    }

    let travelingToPrimary = frListToUseForTooltips.filter((fr) => fr.availability?.location == "PRIMARY");
    let inSecondaryFromHome = frListToUseForTooltips.filter((fr) => fr.availability?.location == "SECONDARY" && fr.availability?.secondaryStartingLocation == "Home");
    let travelingToSecondary = frListToUseForTooltips.filter((fr) => fr.availability?.location == "SECONDARY" && fr.availability?.secondaryStartingLocation == "Travel");

    const getTravellingMessage = (frAvailability: AvailabilityByDay[], label: "primary" | "secondary" | "travel" | "missing-designation") => {
      // sort shows false missing designation on top
      // || check for isAvailable because for the missing designation tooltip we will pass available frs into here some time that dont have a next available day, and we still want them to show
      let filteredAvailability = frAvailability.filter(fr => !!fr.nextAvailableDay || fr.availability.isAvailable).sort((a, b) => Number(a.missingDesignation) - Number(b.missingDesignation));
      
      if (filteredAvailability?.length > 0) {
        let labelExt = "";
        switch (label) {
          case "primary":
          case "secondary":
            labelExt = `in ${label} today`;
            break;
          case "travel":
            labelExt = "traveling today";
            break;
          case "missing-designation":
            labelExt = "missing designation but available today";
            break;
        }

        const firstFRMissingDesignation = filteredAvailability.findIndex(fr => fr.missingDesignation);

        const mapFrToToolTip = (fr: AvailabilityByDay, i: number) => {
          let nameAndDistance = fr.inspector.getDisplayName(fr.nextAvailableDay?.distanceToCase);
          let nextDay = fr.nextAvailableDay ? moment.utc(fr.nextAvailableDay.date).format("MM/DD") : "No next day available";

          let tooltip = `${nameAndDistance}: Next day available: ${nextDay}`;
          
          // strict check on false because not all frs sent into getTravellingMessage come with a missingDesignation set. if its not set we dont want this message
          if (i === 0 && fr.missingDesignation === false) tooltip = "Field Reps below have designation\r\n" + tooltip;
          if (i === firstFRMissingDesignation) tooltip = (i > 0 ? "\r\n" : "") + "Field Reps below are missing designation\r\n" + tooltip;

          return tooltip;
        };

        let tooltip = filteredAvailability.map(mapFrToToolTip).join("\r\n");

        return (
          <div data-tooltip={tooltip}>
            {`${filteredAvailability.length} FR${filteredAvailability.length === 1 ? "" : "s"} ${labelExt}`}
          </div>
        );
      } else {
        return null;
      }
    };

    let excludedNode: ReactNode = null;

    if (hasIneligibleFRs && excludedFRs.length) {
      excludedNode = <span data-tooltip={excludedFRs.map(fr => fr.getDisplayName()).join("\r\n")}>{excludedFRs.length} FR excluded from customer</span>;
    }

    let missignDesignationNode = isShowingMissingDesignationCal ? null : getTravellingMessage(missingDesignationAvailableThisDay, "missing-designation");
    let travellingMessagePrimaryNode = getTravellingMessage(travelingToPrimary, "primary");
    let travellingMessageSecondaryNode = getTravellingMessage(inSecondaryFromHome, "secondary");
    let travellingMessageTravelNode = getTravellingMessage(travelingToSecondary, "travel");

    return (
      <>
        <AvailabilityCalendarDatePicker
          // @ts-ignore
          date={curDate}
          onDateChanged={onDateChanged}
        />
        {!calToShow && <div>No FR Coverage</div>}
        {calToShow}
        {(missignDesignationNode || excludedNode || travellingMessagePrimaryNode || travellingMessageSecondaryNode || travellingMessageTravelNode) && (
          <div className="ineligible-field-reps-container">
            {missignDesignationNode}
            {excludedNode}
            {travellingMessagePrimaryNode}
            {travellingMessageSecondaryNode}
            {travellingMessageTravelNode}
          </div>
        )}
      </>
    );
  };

  return (
    <MCard title="Field Rep List" className="employee-availability-calendar">
      {getCardContent()}
    </MCard>
  );
};

FieldRepAvailabilityView.propTypes = {
  controlIdNumber: PropTypes.number,
  webStatus: PropTypes.string,
};

export default FieldRepAvailabilityView;

export const frScheduleStatusList = [
  SecondaryStatus.UNASSIGNED,
  SecondaryStatus.RECEIVED,
  SecondaryStatus.PREASSIGNMENT,
];
